import { FRANCHISE_MINIMUM_POSITION_COUNTS } from './depth-chart-model';
import {
  calculatePlayerOverall,
  FRANCHISE_JERSEY_NUMBER_RANGES,
  FRANCHISE_PLAYER_POSITIONS,
  type FranchisePlayer,
  type FranchisePlayerPosition,
  type PlayerRatings,
  type PlayerStatKey,
} from './player-model';

/**
 * Mapping from a player NFT's hosted metadata JSON (OpenSea attribute format)
 * to a FranchisePlayer. Used by chain-driven ingestion: minted tokens are
 * materialized into the pool one at a time, so everything here must be
 * deterministic per token — same metadata in, same player out.
 */

export type NftMetadataAttribute = { trait_type?: unknown; value?: unknown };
export type NftPlayerMetadata = { name?: unknown; attributes?: NftMetadataAttribute[] };

const STAT_TRAITS: Record<string, PlayerStatKey> = {
  Athleticism: 'athleticism',
  Strength: 'strength',
  Precision: 'precision',
  Awareness: 'awareness',
  Physicality: 'physicality',
};

/**
 * Cosmetic traits carried into `player.appearance`. Keys are the catalog's
 * trait_type values; values are the appearance keys the simulator client reads
 * to compose the on-field sprite from the hosted species layer art at
 * `assets/sprites/nftPlayers/<Species>/<TraitFolder>/<value>.png` (folders
 * Capitalized, file names lowercase — the client normalizes trait casing).
 */
const APPEARANCE_TRAITS: Record<string, string> = {
  Species: 'species',
  Body: 'body',
  Pants: 'pants',
  Jersey: 'jersey',
  Shoes: 'shoes',
  Gloves: 'gloves',
  Facewear: 'facewear',
  Helm: 'helm',
  Aura: 'aura',
};

function clampRating(value: unknown): number {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return 0;
  }
  return Math.max(0, Math.min(99, Math.round(numeric)));
}

/** Deterministic 32-bit FNV-1a hash — the stand-in for randomness per token. */
function hashToken(tokenId: string): number {
  let hash = 0x811c9dc5;
  for (let index = 0; index < tokenId.length; index += 1) {
    hash ^= tokenId.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

const QUOTA_POSITIONS = FRANCHISE_PLAYER_POSITIONS
  .filter((position) => FRANCHISE_MINIMUM_POSITION_COUNTS[position] > 0);

/**
 * Stateless position assignment for catalogs without a `Position` trait.
 * A pure best-fit funnels everyone into the handful of positions that weight
 * their top stat hardest, so instead: rank the quota positions by this
 * player's positional overall, keep the top few, and pick among them with the
 * token hash weighted by the league position quotas. Stat-coherent per player,
 * roughly league-shaped in aggregate, and stable across re-runs.
 */
function derivePosition(tokenId: string, ratings: PlayerRatings): FranchisePlayerPosition {
  const candidates = QUOTA_POSITIONS
    .map((position) => ({ position, overall: calculatePlayerOverall(position, ratings) }))
    .sort((left, right) => right.overall - left.overall
      || QUOTA_POSITIONS.indexOf(left.position) - QUOTA_POSITIONS.indexOf(right.position))
    .slice(0, 4);

  const totalQuota = candidates
    .reduce((total, { position }) => total + FRANCHISE_MINIMUM_POSITION_COUNTS[position], 0);
  let pick = hashToken(tokenId) % totalQuota;
  for (const { position } of candidates) {
    pick -= FRANCHISE_MINIMUM_POSITION_COUNTS[position];
    if (pick < 0) {
      return position;
    }
  }
  return candidates[0].position;
}

function normalizePositionTrait(value: unknown): FranchisePlayerPosition | null {
  const candidate = String(value ?? '').trim().toUpperCase();
  return (FRANCHISE_PLAYER_POSITIONS as readonly string[]).includes(candidate)
    ? candidate as FranchisePlayerPosition
    : null;
}

export function buildPlayerFromNftMetadata(tokenId: string, meta: NftPlayerMetadata): FranchisePlayer {
  const ratings = { athleticism: 0 } as PlayerRatings;
  const appearance: Record<string, string> = {};
  let rarity = 4;
  let positionTrait: FranchisePlayerPosition | null = null;

  for (const attribute of Array.isArray(meta.attributes) ? meta.attributes : []) {
    const trait = String(attribute?.trait_type || '');
    const statKey = STAT_TRAITS[trait];
    const appearanceKey = APPEARANCE_TRAITS[trait];
    if (statKey) {
      ratings[statKey] = clampRating(attribute.value);
    } else if (appearanceKey) {
      const value = String(attribute?.value ?? '').trim();
      if (value) {
        appearance[appearanceKey] = value;
      }
    } else if (trait === 'Recruit') {
      rarity = String(attribute.value || '').startsWith('5') ? 5 : 4;
    } else if (trait === 'Position') {
      // Catalogs that carry an explicit Position trait are authoritative;
      // the hash-draft below is only the fallback for those that don't.
      positionTrait = normalizePositionTrait(attribute.value);
    }
  }

  const position = positionTrait ?? derivePosition(tokenId, ratings);
  const [min, max] = FRANCHISE_JERSEY_NUMBER_RANGES[position];
  const numericToken = Number.parseInt(tokenId, 10);

  return {
    id: tokenId,
    name: typeof meta.name === 'string' && meta.name.trim()
      ? meta.name.trim()
      : `DFL Player #${tokenId}`,
    position,
    years: 1,
    number: min + ((Number.isFinite(numericToken) ? numericToken : hashToken(tokenId)) % (max - min + 1)),
    overall: calculatePlayerOverall(position, ratings),
    ratings,
    rarity,
    ...(Object.keys(appearance).length ? { appearance } : {}),
  };
}
